import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Loader2, Download, Share2 } from 'lucide-react';
import usePWAInstall from '@/hooks/usePWAInstall';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithPin } = useAuth();
  const pwa = usePWAInstall();
  const [loginMethod, setLoginMethod] = useState('password');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    pin: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (loginMethod === 'pin') {
        await loginWithPin(loginData.email, loginData.pin);
      } else {
        await login(loginData.email, loginData.password);
      }
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left side - Hero */}
      <div 
        className="login-hero hidden lg:flex"
        style={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)'
        }}
      >
        <div className="max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">GS</span>
            </div>
            <span className="text-white text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              CRM Sync
            </span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sync Your CRM Data Effortlessly
          </h1>
          <p className="text-white/80 text-lg leading-relaxed">
            Connect your CRM locations, sync custom fields and calendars, 
            and manage everything from one central dashboard.
          </p>
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span>Multi-location support</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span>Real-time webhook sync</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span>Calendar availability management</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2 mb-2 lg:hidden">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">GS</span>
              </div>
              <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                CRM Sync
              </span>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Button
                type="button"
                variant={loginMethod === 'password' ? 'default' : 'outline'}
                className={loginMethod === 'password' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                onClick={() => setLoginMethod('password')}
              >
                Password
              </Button>
              <Button
                type="button"
                variant={loginMethod === 'pin' ? 'default' : 'outline'}
                className={loginMethod === 'pin' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                onClick={() => setLoginMethod('pin')}
              >
                PIN
              </Button>
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome Back
            </CardTitle>
            <CardDescription>
              Sign in to your account to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  data-testid="login-email"
                />
              </div>
              {loginMethod === 'password' ? (
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="login-pin">4-digit PIN</Label>
                  <Input
                    id="login-pin"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="••••"
                    value={loginData.pin}
                    onChange={(e) => {
                      const v = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                      setLoginData({ ...loginData, pin: v });
                    }}
                    required
                    data-testid="login-pin"
                  />
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
              <p className="text-sm text-slate-500 text-center">
                Need an account? Contact your administrator.
              </p>
              {(pwa.canInstall || pwa.isIos) && !pwa.isStandalone && (
                <button
                  type="button"
                  onClick={async () => {
                    if (pwa.canInstall) {
                      await pwa.install();
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 text-xs text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 rounded-lg py-2 transition-colors"
                  data-testid="login-install-app"
                >
                  {pwa.canInstall ? (
                    <>
                      <Download className="w-3.5 h-3.5" />
                      <span className="font-medium">Install CRM Sync app</span>
                      <span className="text-indigo-400">— faster access on your phone</span>
                    </>
                  ) : (
                    <>
                      <Share2 className="w-3.5 h-3.5" />
                      <span className="font-medium">Install on iPhone:</span>
                      <span className="text-indigo-400">Share ▸ Add to Home Screen</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
